import { useSearchIndustries } from '@securecore-new-application/securecore-datacore';
import { Industry, IndustryTypes } from '@securecore-new-application/securecore-datacore/lib/types';
import { Select, Spin } from 'antd';
import type { SelectProps } from 'antd/es/select';
import debounce from 'lodash/debounce';
import React, { FC, useEffect, useMemo, useState } from 'react';

import { debouncedWait } from '../../constants';

export interface IndustrySearchValue {
  label: string;
  value: number;
}

export interface IndustriesSearchProps extends Omit<SelectProps<IndustrySearchValue[]>, 'options' | 'children'> {
  debounceTimeout?: number;
  type?: IndustryTypes;
  onChange(value: IndustrySearchValue[]): void;
}

export const IndustriesSearch: FC<IndustriesSearchProps> = ({ debounceTimeout = debouncedWait, type, ...props }) => {
  const [search, { data: industries, loading }] = useSearchIndustries();
  const [options, setOptions] = useState<IndustrySearchValue[]>();

  useEffect(() => {
    const industryOptions: IndustrySearchValue[] = (industries?.searchIndustries || []).map(
      (i: Industry) =>
        ({
          value: i.id,
          label: i.name,
        } as IndustrySearchValue)
    );

    setOptions(industryOptions);
  }, [industries?.searchIndustries]);

  useEffect(() => {
    let searchData;

    searchData = { criteria: '' };
    if (type) {
      searchData = { criteria: '', type };
    }

    search({ variables: { data: searchData } });
  }, [search, type]);

  const debounceFetcher = useMemo(() => {
    const loadOptions = async (value: string) => {
      let searchData;

      searchData = { criteria: value };
      if (type) {
        searchData = { criteria: value, type };
      }

      await search({ variables: { data: searchData } });
    };

    return debounce(loadOptions, debounceTimeout);
  }, [debounceTimeout, search, type]);

  return (
    <Select
      mode="multiple"
      placeholder="Type to search industries..."
      labelInValue
      filterOption={false}
      onSearch={debounceFetcher}
      notFoundContent={loading ? <Spin size="small" /> : null}
      {...props}
      options={options}
    />
  );
};
